import * as React from "react"
import { cn } from "@/lib/class-control"
import { ControllerRenderProps, FieldValues } from "react-hook-form"
import { IMaskInput } from "react-imask"

interface CurrencyInputProps<T extends FieldValues> {
  field: ControllerRenderProps<T>
  placeholder?: string
  className?: string
  disabled?: boolean
}

export default function CurrencyInput<T extends FieldValues>({
  field,
  placeholder = "R$ 0,00",
  className,
  disabled = false,
}: CurrencyInputProps<T>) {
  const formatCurrency = (value: number | string): string => {
    if (!value) return ""

    const numValue = typeof value === "string" ? parseFloat(value) : value
    if (isNaN(numValue)) return ""

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numValue)
  }

  const parseCurrency = (value: string): number => {
    // Remove tudo exceto números e vírgula
    const cleaned = value.replace(/[^\d,]/g, "")
    // Substitui vírgula por ponto
    const withDot = cleaned.replace(",", ".")
    const parsed = parseFloat(withDot)
    return isNaN(parsed) ? 0 : parsed
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value
    const numericValue = parseCurrency(rawValue)
    field.onChange(numericValue)
  }

  const displayValue = formatCurrency(field.value)

  return (
    <div
      className={cn(
        "flex items-center w-full px-3 py-2 rounded-md bg-transparent text-white",
        "border border-gray-600 focus-within:ring-1 focus-within:ring-primary",
        className,
        disabled && "opacity-50 bg-[#0c1427] cursor-not-allowed"
      )}
    >
      <IMaskInput
        mask={Number}
        thousandsSeparator="."
        radix=","
        scale={2}
        className="bg-transparent border-none outline-none text-gray-200 text-sm font-semibold w-full"
        value={field.value == null ? "" : String(field.value)}
        onAccept={(value) => field.onChange(parseCurrency(value as string))}
        name={field.name}
        inputRef={field.ref}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  )
}

CurrencyInput.displayName = "CurrencyInput"
